import {
  QueryClient,
  QueryClientProvider,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { lazy, Suspense, useEffect, useState } from "react";
import { Logotype } from "@/components/brand/Logotype";
import { FacultyListSkeleton, ShellSkeleton } from "@/components/ui/Skeleton";
import { AppShell } from "@/components/shell/AppShell";
import { Button } from "@/components/ui/Button";
import { Field } from "@/components/ui/Field";
import { Tabs } from "@/components/ui/Tabs";
import { FournisseurDeToasts } from "@/components/ui/Toasts";
import { VerificationBanner } from "@/features/account/VerificationBanner";
import { FeedScreen } from "@/features/feed/FeedScreen";
import { Filet } from "@/components/shell/Filet";
import { preparerLeSon } from "@/lib/son";
import { FournisseurDuVisage, VisageDeLEntete } from "@/features/feed/Visage";
import {
  FournisseurDuFil,
  RafraichirDepuisLEntete,
} from "@/features/feed/CanalDuFil";
import { PorteDeLaVitrine } from "@/features/vitrine/PorteDeLaVitrine";
import { veutSeConnecter } from "@/features/vitrine/routes";
import { Icone } from "@/components/ui/Icone";
import { rechargerVers } from "@/lib/navigation";
import { BandeauDeMaintenance } from "@/features/maintenance/BandeauDeMaintenance";
import { PorteDeMaintenance } from "@/features/maintenance/PorteDeMaintenance";

/**
 * **Les écrans se chargent quand on y va** — PR-2.
 *
 * Ils étaient tous importés d'emblée : chaque candidat téléchargeait le
 * back-office, les archives, le classement et la vitrine de design avant de
 * voir sa première carte. Sur un forfait compté et une connexion haïtienne,
 * c'est du temps et des octets payés pour des écrans que la plupart n'ouvriront
 * jamais — le back-office ne concerne qu'une poignée de comptes.
 *
 * **Le Fil, lui, reste EAGER.** C'est l'écran d'arrivée : le charger en second
 * temps ajouterait un aller-retour là où le candidat attend déjà. Le découpage
 * doit accélérer la première carte, pas la retarder.
 *
 * `VerificationBanner` et le visage restent eager aussi : ils font partie de la
 * coque, et un bandeau qui apparaît une seconde après la page se lit comme un
 * défaut d'affichage.
 */
const AccountPanel = lazy(async () => ({
  default: (await import("@/features/account/AccountPanel")).AccountPanel,
}));
const VerifyEmailPage = lazy(async () => ({
  default: (await import("@/features/account/VerifyEmailPage")).VerifyEmailPage,
}));
const Atelier = lazy(async () => ({
  default: (await import("@/features/editorial/Atelier")).Atelier,
}));
const AmisScreen = lazy(async () => ({
  default: (await import("@/features/amis/AmisScreen")).AmisScreen,
}));
const ExercicesScreen = lazy(async () => ({
  default: (await import("@/features/exercices/ExercicesScreen"))
    .ExercicesScreen,
}));

const ArchivesScreen = lazy(async () => ({
  default: (await import("@/features/archives/ArchivesScreen")).ArchivesScreen,
}));
const NotificationsScreen = lazy(async () => ({
  default: (await import("@/features/notifications/NotificationsScreen"))
    .NotificationsScreen,
}));
const ParcoursScreen = lazy(async () => ({
  default: (await import("@/features/parcours/ParcoursScreen")).ParcoursScreen,
}));
const Classement = lazy(async () => ({
  default: (await import("@/features/leaderboard/Classement")).Classement,
}));
const ConcoursActif = lazy(async () => ({
  default: (await import("@/features/onboarding/ConcoursActif")).ConcoursActif,
}));
const FacultyPicker = lazy(async () => ({
  default: (await import("@/features/onboarding/FacultyPicker")).FacultyPicker,
}));
const ThemesSection = lazy(async () => ({
  default: (await import("@/features/tags/ThemesSection")).ThemesSection,
}));

/**
 * « Mon abonnement » — [DECISIONS §157, §165].
 *
 * En second temps comme les autres : on n'y va qu'au démarrage de l'essai, à
 * son terme, et au moment d'acheter. Le charger d'office ferait payer une
 * grille tarifaire à chaque ouverture du Fil, sur un forfait au mégaoctet.
 */
const MonAbonnement = lazy(async () => ({
  default: (await import("@/features/abonnement/MonAbonnement")).MonAbonnement,
}));

/**
 * **Les deux écrans d'avant le compte** — [DECISIONS §159].
 *
 * En second temps comme les autres, et pour une raison de plus : ni l'un ni
 * l'autre ne sert au candidat qui revient. `Inscription` porte les trois
 * étapes de la vérification d'adresse et le champ téléphone ;
 * `MotDePasseOublie`, la demande de réinitialisation. Le visiteur qui arrive
 * pour se connecter n'a besoin d'aucun des deux — et c'est le cas le plus
 * fréquent, l'onglet « Se connecter » étant déjà dans la coque.
 */
const Inscription = lazy(async () => ({
  default: (await import("@/features/onboarding/Inscription")).Inscription,
}));
const MotDePasseOublie = lazy(async () => ({
  default: (await import("@/features/onboarding/MotDePasseOublie"))
    .MotDePasseOublie,
}));

/**
 * La vitrine de design — jamais servie à un candidat.
 *
 * Elle n'existe que pour l'équipe, sur `/design`, et pesait pourtant dans le
 * paquet de tout le monde.
 */
const DesignPage = lazy(async () => ({
  default: (await import("./DesignPage")).DesignPage,
}));
import {
  AccesTermine,
  BandeauAccesTermine,
} from "@/features/abonnement/AccesTermine";
import * as abo from "@/lib/api/abonnement";
import * as auth from "@/lib/api/auth";
import type { Me } from "@/lib/api/auth";
import type { Emplacement } from "@/lib/api/exercices";
import {
  ApiError,
  oublierLaDeconnexion,
  raisonDeLaDeconnexion,
} from "@/lib/api/client";
import { brancherLaFile } from "@/lib/hors-ligne/file";
import { applyTheme } from "@/lib/theme/theme";
import { ChaineWhatsApp } from "@/components/brand/ChaineWhatsApp";
import { bienvenue, oublierLaBienvenue, retenirLaBienvenue } from "@/features/onboarding/bienvenue";
import { verificationEnCours } from "@/features/onboarding/demandeEnCours";
import { couponEnAttente, retenirLeCoupon } from "@/features/abonnement/couponEnAttente";
import { codeDuLien } from "@/features/abonnement/lienDeCoupon";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false },
  },
});

/**
 * **Le sommet de l'arbre, et rien d'autre** — [DECISIONS §338].
 *
 * Les toasts enveloppent TOUT, y compris les deux écrans qui court-circuitent
 * la coque — la vitrine de design et l'atterrissage du lien de courriel. Deux
 * raisons, et la seconde est la vraie :
 *
 *   1. les deux régions `aria-live` doivent exister AVANT le premier message.
 *      Une région insérée en même temps que son contenu n'est pas annoncée : un
 *      lecteur d'écran n'observe que celles qu'il connaissait déjà ;
 *   2. `/design` est l'endroit où l'on REGARDE un composant dans ses états
 *      rares (06 §6). Un toast posé sous le branchement n'y serait pas
 *      montable, et « un état qu'on ne peut pas regarder est un état qu'on ne
 *      corrige pas ».
 */
export function App() {
  return (
    <FournisseurDeToasts>
      <Ecrans />
    </FournisseurDeToasts>
  );
}

function Ecrans() {
  /*
   * **Le lien d'un coupon** — [DECISIONS §330]. `/coupon/4KJ9P2WDMX`.
   *
   * **Le code est mis à l'abri AVANT tout rendu, et avant toute session.** Un
   * lycéen qui reçoit ce lien sur WhatsApp n'a le plus souvent pas de compte :
   * l'application va lui montrer l'inscription, il va prouver son adresse, et
   * pendant ces trois écrans le chemin ne le suivra pas. C'est le même défaut
   * que le §324 — « le code sous les yeux, et nulle part où le taper ».
   *
   * **Ici, et pas dans un effet.** Un effet ne se déclenche qu'APRÈS le premier
   * rendu ; l'écran d'abonnement, lui, lit le code retenu à SON montage, et
   * l'ordre des deux ne se garantit pas. L'écriture est idempotente — le même
   * code, dans la même clé — et cette fonction ne se rend qu'une fois.
   *
   * Aucun écran propre à ce chemin : `ongletDArrivee` ouvre « Mon abonnement »,
   * qui porte déjà le champ, l'échange et le compteur. Une page de plus aurait
   * dupliqué les trois.
   */
  const codeRecu = codeDuLien(window.location.pathname);

  if (codeRecu !== null) retenirLeCoupon(codeRecu);

  // La vitrine n'a ni compte, ni session, ni requête : la brancher sur le
  // chemin évite d'imposer un routeur à une application qui, pour l'instant,
  // n'a qu'un seul écran authentifié. Le jour où les lots suivants en
  // demanderont un, cette ligne disparaîtra dans ses routes.
  if (window.location.pathname === "/design") {
    return (
      <Suspense fallback={<ShellSkeleton />}>
        <DesignPage />
      </Suspense>
    );
  }

  // RG-1.3 — l'atterrissage du lien de courriel. Sans session : le candidat
  // relève souvent ses courriels sur un autre appareil.
  if (window.location.pathname === "/verifier-adresse") {
    return (
      <Suspense fallback={<ShellSkeleton />}>
        <VerifyEmailPage />
      </Suspense>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      {/* **Le seul point de branchement de la vitrine** — [DECISIONS §194].
          Quel chemin lui appartient, quel écran il ouvre et à quelle condition :
          tout est dans `features/vitrine/`. Ici, une ligne. Elle rend la coque
          telle quelle sur tout ce qui n'est pas à elle — et sur `/`, tant que
          l'on ne sait pas encore s'il y a une session, pour ne pas resérialiser
          le démarrage que PR-2 a rendu parallèle. */}
      {/* **Le seul point de branchement de la maintenance** — [DECISIONS §196].
          AUTOUR de la vitrine, et pas dedans : un visiteur sans compte doit
          apprendre que le site est fermé plutôt que de tomber sur un accueil
          dont plus aucun bouton ne répond (RG-9.13). Elle rend la coque telle
          quelle tant qu'elle ne sait rien, et ne reprend JAMAIS la page à une
          application déjà chargée — c'est le bandeau qui s'en charge, pour ne
          pas démonter l'épreuve en cours (RG-9.12). */}
      <PorteDeMaintenance
        coque={
          <PorteDeLaVitrine
            chemin={window.location.pathname}
            coque={<Shell />}
          />
        }
      />
    </QueryClientProvider>
  );
}

function Shell() {
  const { data: me, isLoading } = useQuery({
    queryKey: ["me"],
    queryFn: auth.me,
    retry: false,
  });

  /**
   * **Les facultés partent EN MÊME TEMPS que le compte** — PR-2.
   *
   * Cette requête vivait dans `Session`, qui ne se monte qu'une fois `/me`
   * résolu : le démarrage enchaînait donc `/me`, puis `/me/faculties`, puis
   * seulement `/feed`. Trois allers-retours en file indienne — près d'une
   * seconde de pure latence sur une connexion haïtienne avant que la première
   * carte soit seulement demandée.
   *
   * Les deux ne dépendent pourtant l'une de l'autre en rien : toutes deux ne
   * demandent que le cookie de session. Les lancer ensemble retire un
   * aller-retour de CHAQUE démarrage à froid.
   *
   * `enabled` reste absent volontairement : la requête part avant même de
   * savoir s'il y a une session. Si le candidat n'est pas connecté, elle rend
   * 401 et personne ne la lit — un aller-retour perdu pour un visiteur, contre
   * un aller-retour gagné pour tous ceux qui reviennent.
   */
  const { isLoading: chargementSiennes } = useQuery({
    queryKey: ["me", "faculties"],
    queryFn: auth.myFaculties,
    retry: false,
  });

  useEffect(() => {
    if (me === undefined) return;
    applyTheme(me.preferences.theme, me.preferences.accent);
  }, [me]);

  // ── OUVRIR L'AUDIO AU PREMIER GESTE — §392 ────────────────────────────────
  //
  // **Le son du Fil ne part jamais pendant un geste** : il part quand le
  // serveur répond, dans une promesse. Les navigateurs de téléphone créent
  // alors le contexte audio SUSPENDU et ignorent le `resume()` qui suit — les
  // notes se posent sur une horloge gelée, à n'importe quel volume.
  //
  // Ce déclencheur est le seul moment sûr : le premier contact du candidat avec
  // la page, quel qu'il soit. `preparerLeSon` ne travaille qu'une fois, et le
  // tampon qu'elle joue est muet.
  useEffect(() => {
    const ouvrir = (): void => {
      preparerLeSon();
    };

    // `pointerdown` couvre la souris et le tactile ; `keydown` couvre la
    // navigation au clavier, qui n'émet aucun pointeur.
    document.addEventListener("pointerdown", ouvrir, { passive: true });
    document.addEventListener("keydown", ouvrir, { passive: true });

    return () => {
      document.removeEventListener("pointerdown", ouvrir);
      document.removeEventListener("keydown", ouvrir);
    };
  }, []);

  if (isLoading) return <ShellSkeleton />;

  if (me === undefined) {
    return (
      <div className="mx-auto flex min-h-full max-w-[600px] flex-col px-4">
        <AuthScreen />
      </div>
    );
  }

  // Le squelette est peint ICI tant que les facultés n'ont pas répondu : la
  // décision « tunnel ou application » en dépend, et trancher trop tôt
  // ferait clignoter le tunnel devant un candidat déjà installé.
  if (chargementSiennes) return <ShellSkeleton />;

  return <Session me={me} />;
}

/**
 * L'écran d'entrée — inscription, connexion, mot de passe oublié.
 *
 * **L'inscription a quitté ce formulaire** — [DECISIONS §159]. Elle se fait
 * maintenant en trois temps, dans `Inscription`, et ne crée un compte qu'une
 * fois l'adresse prouvée. Ce qui reste ici est la CONNEXION, plus une porte
 * vers la réinitialisation.
 */
function AuthScreen() {
  const client = useQueryClient();
  // §167 — le motif de la déconnexion subie, s'il y en a un. Lu une seule fois,
  // au montage : le garder à jour ferait réapparaître le message après une
  // reconnexion réussie, c'est-à-dire au moment où il n'est plus vrai.
  const [motif] = useState(raisonDeLaDeconnexion);
  /*
   * **Le coupon suivi par un visiteur sans compte** — [DECISIONS §330].
   *
   * Lu une seule fois, à l'initialisation, comme le motif juste au-dessus : le
   * relire à chaque rendu ferait disparaître le message à l'instant précis où
   * l'échange l'efface, c'est-à-dire pendant que le candidat le lit.
   */
  const [coupon] = useState(couponEnAttente);
  /**
   * L'onglet d'arrivée — [DECISIONS §220].
   *
   * `register` par défaut, parce que la plupart de ceux qui atterrissent ici
   * n'ont pas de compte. Mais celui qui a tapé « Se connecter » sur l'accueil
   * arrive avec `?se-connecter` dans l'adresse, et il doit trouver le
   * formulaire qu'il a demandé — pas six champs d'inscription et un second
   * onglet à découvrir.
   *
   * Lu une seule fois, à l'initialisation : changer d'onglet ensuite ne doit
   * pas réécrire l'adresse, et l'adresse ne doit pas reprendre la main.
   */
  const [mode, setMode] = useState<"register" | "login">(
    /*
     * **Une vérification en cours l'emporte sur l'adresse** — [DECISIONS §324].
     *
     * Le candidat arrivé par « Se connecter » garde `?se-connecter` dans son
     * adresse même après avoir basculé sur l'onglet d'inscription. Il demande
     * son code, quitte l'écran pour le lire, le navigateur du téléphone
     * décharge la page — et au rechargement, c'est la CONNEXION qui s'ouvrait,
     * avec le code sous les yeux et nulle part où le taper.
     *
     * L'adresse dit d'où l'on vient ; la vérification en cours dit ce qu'on est
     * en train de faire. La seconde est plus récente, et c'est elle qui gagne.
     */
    verificationEnCours() !== null
      ? "register"
      : veutSeConnecter(window.location.search)
        ? "login"
        : "register",
  );
  const [errors, setErrors] = useState<Record<string, string[]>>({});

  /**
   * **Ce qui n'a pas de champ où s'écrire** — [DECISIONS §294].
   *
   * Une panne totale de connexion l'a montré le 20 août 2026 : `/login` rendait
   * 500, et un 500 ne porte AUCUN `errors`. L'écran ne montrait donc rien —
   * ni erreur, ni progression, ni rien. Le candidat tapait, appuyait, et il ne
   * se passait rien. Sept fois de suite, d'après les journaux.
   *
   * Un formulaire qui échoue en silence est pire qu'un formulaire qui se
   * trompe : au moins l'erreur dit qu'on a été entendu.
   */
  const [panne, setPanne] = useState<string | null>(null);

  /** L'adresse du compte qu'on vient de créer, pour l'annoncer et la pré-écrire. */
  const [compteCree, setCompteCree] = useState<string | null>(null);
  // RG-1.4 — le second facteur n'apparaît QUE si le serveur le réclame :
  // l'afficher d'emblée ferait chercher un code aux comptes qui n'en ont pas.
  const [deuxiemeFacteur, setDeuxiemeFacteur] = useState(false);
  const [oubli, setOubli] = useState(false);

  const submit = useMutation({
    mutationFn: async (form: FormData) => {
      setErrors({});
      setPanne(null);
      // FormData.get renvoie `string | File` : String(file) donnerait
      // « [object File] ». On ne lit que les valeurs textuelles.
      const value = (key: string): string => {
        const raw = form.get(key);

        return typeof raw === "string" ? raw : "";
      };

      const code = value("code");

      return auth.login(
        value("email"),
        value("password"),
        code === "" ? undefined : code,
      );
    },
    onSuccess: (user: Me) => {
      // Le motif a servi : le garder ferait réapparaître « votre compte a été
      // utilisé ailleurs » à la prochaine déconnexion ordinaire.
      oublierLaDeconnexion();
      client.setQueryData(["me"], user);
    },
    onError: (error: unknown) => {
      // **Le réseau, ou nous.** Une requête qui n'aboutit pas ne lève pas une
      // `ApiError` : sans cette branche, l'écran restait muet exactement quand
      // le candidat avait le plus besoin qu'on lui parle.
      if (!(error instanceof ApiError)) {
        setPanne(
          "Impossible de joindre Haicademy. Vérifie ta connexion, puis réessaie.",
        );

        return;
      }

      // 423 — le mot de passe est bon, le second facteur manque. Ce n'est pas
      // une erreur de saisie : on ouvre le champ sans rien signaler en rouge.
      if (error.status === 423) {
        setDeuxiemeFacteur(true);

        return;
      }

      const body =
        typeof error.body === "object" && error.body !== null
          ? (error.body as {
              errors?: Record<string, string[]>;
              message?: string;
            })
          : {};

      // Un 422 nomme le champ fautif ; tout le reste — 500, 419, 429 — n'a
      // aucun champ où s'écrire, et doit donc se dire en toutes lettres.
      if (body.errors !== undefined && Object.keys(body.errors).length > 0) {
        setErrors(body.errors);

        return;
      }

      setPanne(
        error.status >= 500
          ? "Quelque chose a échoué de notre côté. Réessaie dans un instant — " +
              "si cela persiste, écris-nous."
          : (body.message ?? "La demande n’a pas abouti. Réessaie."),
      );
    },
  });

  const first = (key: string) => errors[key]?.[0];

  return (
    <main className="flex flex-col gap-6 pt-10">
      <header className="flex flex-col gap-3">
        {/* Le logotype COMPLET — 06 §8. C'est le premier écran qu'un candidat
            voit : la marque seule ne lui apprendrait pas le nom du produit. Le
            `h1` tient son nom accessible du mot, qui est du vrai texte. */}
        <h1>
          <Logotype taille={31} />
        </h1>
        <p className="text-[15px]" style={{ color: "var(--texte-2)" }}>
          Chak jou, yon pa.
        </p>

        {/* **Le retour à l'accueil manquait, et c'était un cul-de-sac** —
            [DECISIONS §220]. Cet écran s'atteint depuis la vitrine par un vrai
            changement de document : le bouton « précédent » du navigateur y
            ramène, mais rien à l'écran ne le disait, et un visiteur venu voir
            le prix se retrouvait devant un formulaire sans porte de sortie. Le
            logotype juste au-dessus ne clique pas ici — il est le titre de la
            page, pas un lien. */}
        <button
          type="button"
          onClick={() => {
            rechargerVers("/");
          }}
          className="flex min-h-11 items-center gap-2 self-start text-[13px]"
          style={{ color: "var(--texte-2)" }}
        >
          <Icone nom="fleche-gauche" taille={16} />
          Retour à l’accueil
        </button>
      </header>

      {/* **La session fermée dit pourquoi** — [DECISIONS §157, §167].
          Sans ce report, le candidat chassé par une connexion faite ailleurs
          retombait ici sans un mot : la première explication qui lui vient est
          que l'application est cassée, la seconde qu'on lui a volé son compte.
          Ni l'une ni l'autre n'est vraie, et aucune des deux ne le fait
          revenir. */}
      {motif !== null && (
        <p
          role="status"
          className="rounded-2xl border p-3 text-[15px]"
          style={{ borderColor: "var(--bordure)", color: "var(--texte-2)" }}
        >
          {motif}
        </p>
      )}

      {/* **Le coupon suivi sans compte** — [DECISIONS §330].
          Sans ce mot, le lycéen qui clique sur le lien reçu par WhatsApp tombe
          sur six champs d'inscription et n'a aucune raison de croire que son
          code a survécu au trajet : il ressort de l'application pour aller le
          relire, et la moitié ne revient pas. Le code EST mis de côté — c'est
          ce que dit cette phrase, et c'est vrai. */}
      {coupon !== null && (
        <p
          role="status"
          className="rounded-2xl border p-3 text-[15px]"
          style={{ borderColor: "var(--accent)", color: "var(--texte-1)" }}
        >
          Ton coupon <strong className="font-mono">{coupon}</strong> est gardé de
          côté. Crée ton compte : il t’attendra dans « Mon abonnement ».
        </p>
      )}

      {oubli ? (
        <Suspense fallback={<ShellSkeleton />}>
          <MotDePasseOublie
            onFini={() => {
              setOubli(false);
              setMode("login");
              setErrors({});
            }}
          />
        </Suspense>
      ) : (
        <>
          <Tabs
            tabs={[
              { id: "register", label: "Créer un compte" },
              { id: "login", label: "Se connecter" },
            ]}
            active={mode}
            onChange={(id) => {
              setMode(id as "register" | "login");
              setErrors({});
              setDeuxiemeFacteur(false);
            }}
          />

          {mode === "register" ? (
            <Suspense fallback={<ShellSkeleton />}>
              <Inscription
                onCompteCree={(
                  adresse: string,
                  sessionOuverte: boolean,
                  pseudo: string,
                ) => {
                  setErrors({});
                  setPanne(null);

                  /*
                   * **On entre directement** — [DECISIONS §317]. Le serveur a
                   * ouvert la session : redemander le mot de passe choisi il y
                   * a quatre secondes serait une porte de plus, au moment
                   * précis où le candidat est le plus près d'entrer.
                   *
                   * `invalidateQueries(['me'])` suffit à faire basculer
                   * l'application : c'est cette requête qui décide entre le
                   * tunnel d'accueil et l'écran de connexion. Le message de
                   * bienvenue est porté par `bienvenue`, que la coque affiche
                   * une fois entrée.
                   */
                  if (sessionOuverte) {
                    retenirLaBienvenue(pseudo);
                    void client.invalidateQueries({ queryKey: ["me"] });

                    return;
                  }

                  // Le repli du 20 août : la session n'a pas pu s'ouvrir, mais
                  // le compte EXISTE. On conduit à la connexion, adresse déjà
                  // écrite, plutôt que d'entrer nulle part.
                  setCompteCree(adresse);
                  setMode("login");
                }}
              />
            </Suspense>
          ) : (
            <form
              className="flex flex-col gap-4"
              onSubmit={(event) => {
                event.preventDefault();
                submit.mutate(new FormData(event.currentTarget));
              }}
            >
              {compteCree !== null && (
                <p
                  role="status"
                  className="rounded-2xl border p-3 text-[14px]"
                  style={{
                    borderColor: "var(--bordure)",
                    color: "var(--texte-2)",
                  }}
                >
                  Ton compte est créé. Connecte-toi avec le mot de passe que tu
                  viens de choisir.
                </p>
              )}

              {panne !== null && (
                // `role="alert"` : le lecteur d'écran l'annonce sans qu'on ait
                // à déplacer le focus, qui doit rester dans le formulaire.
                <p
                  role="alert"
                  className="rounded-2xl border p-3 text-[14px]"
                  style={{ borderColor: "var(--faux)", color: "var(--faux)" }}
                >
                  {panne}
                </p>
              )}

              <Field
                label="Adresse e-mail"
                name="email"
                type="email"
                required
                autoComplete="email"
                // Retaper l'adresse qu'on vient de prouver serait une occasion
                // de se tromper, juste après en avoir reçu le code.
                defaultValue={compteCree ?? undefined}
                error={first("email")}
              />
              <Field
                label="Mot de passe"
                name="password"
                type="password"
                required
                autoComplete="current-password"
                error={first("password")}
              />

              {deuxiemeFacteur && (
                <Field
                  label="Code de vérification"
                  name="code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                  hint="Six chiffres depuis ton application, ou un code de secours."
                  error={first("code")}
                />
              )}

              <Button type="submit" loading={submit.isPending}>
                Se connecter
              </Button>

              {/* Discret, et sous le bouton : c'est une sortie de secours, pas
                  une invitation. Absente, elle laissait un candidat qui a perdu
                  son mot de passe sans aucun recours dans l'application. */}
              <Button
                variant="ghost"
                size="dense"
                className="self-start"
                onClick={() => {
                  setOubli(true);
                  setErrors({});
                  setDeuxiemeFacteur(false);
                }}
              >
                Mot de passe oublié
              </Button>
            </form>
          )}

          {/* **Sous les deux panneaux, jamais au-dessus** — [DECISIONS §313].
              Le premier travail de cet écran est de faire entrer le candidat ;
              ce qui se place avant le formulaire lui dispute l'attention. Ici,
              la chaîne s'adresse à qui hésite encore, à qui vient de créer son
              compte, et à qui revient sans avoir décidé de payer. */}
          <ChaineWhatsApp />
        </>
      )}
    </main>
  );
}

/**
 * Entrée dans l'application — F1.2 puis F1.3.
 *
 * Le tunnel d'accueil s'affiche SANS la coque : tant que le candidat n'a pas
 * choisi ses facultés, une barre d'onglets vers un fil vide ne lui servirait
 * qu'à se perdre. Twitter fait de même pour son propre accueil.
 */
/**
 * « Bienvenue, wideline. Ton compte est créé. » — [DECISIONS §317].
 *
 * **Il se lit une fois.** Un accueil qui reparaît à chaque retour sur l'écran
 * cesse d'être un accueil et devient un meuble : `useEffect` l'oublie dès qu'il
 * a été affiché, et l'état local le garde à l'écran pour cette visite-là.
 *
 * `role="status"` et non `alert` : c'est une bonne nouvelle, elle ne doit pas
 * interrompre ce que le lecteur d'écran est en train de dire.
 */
function MotDeBienvenue() {
  const [pseudo] = useState(bienvenue);

  useEffect(() => {
    oublierLaBienvenue();
  }, []);

  if (pseudo === null) return null;

  return (
    <p
      role="status"
      className="rounded-2xl border p-3 text-[15px]"
      style={{ borderColor: "var(--accent)", color: "var(--texte-1)" }}
    >
      Bienvenue, <span className="font-bold">{pseudo}</span> — ton compte est
      créé et tu es connecté. Choisis maintenant le concours que tu prépares.
    </p>
  );
}

function Session({ me }: { me: Me }) {
  const [etape, setEtape] = useState<
    "facultes" | "themes" | "application" | null
  >(null);
  const client = useQueryClient();

  const { data: faculties = [], isLoading: chargementCatalogue } = useQuery({
    queryKey: ["faculties"],
    queryFn: auth.faculties,
  });

  // Un candidat qui a déjà choisi ses facultés ne repasse PAS par l'accueil :
  // le tunnel est une porte d'entrée, pas un péage à chaque connexion.
  const { data: siennes, isLoading: chargementSiennes } = useQuery({
    queryKey: ["me", "faculties"],
    queryFn: auth.myFaculties,
  });

  const choisir = useMutation({
    mutationFn: ({
      slugs,
      primary,
      options,
    }: {
      slugs: string[];
      primary: string;
      options: Record<string, string>;
    }) => auth.chooseFaculties(slugs, primary, options),
    /**
     * **Le choix invalide ce qu'il vient de démentir** — [DECISIONS §304].
     *
     * L'écran suivant est celui des matières, et il lit `['tags', …]` ; le
     * périmètre de ces arbres vient d'être décidé par cette requête. Sans
     * invalidation, TanStack les sert depuis le cache pendant `staleTime`,
     * soit trente secondes : le candidat compose son fil dans l'arbre d'AVANT
     * son choix, qui n'était borné par rien.
     *
     * `['me', 'faculties']` avec, parce que `siennes` décide du retour dans le
     * tunnel : laissé vide, un candidat qui a bien choisi y serait renvoyé.
     *
     * Le même geste existait déjà dans `MonConcours` — les deux chemins vers
     * `chooseFaculties` avaient divergé, et c'est celui de l'inscription qui
     * avait tort. On attend les deux avant d'avancer : l'écran des matières
     * refait sa requête au montage, pas au retour d'une invalidation tardive.
     */
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: ["me", "faculties"] }),
        client.invalidateQueries({ queryKey: ["tags"] }),
      ]);

      setEtape("themes");
    },
  });

  if (chargementSiennes) return <ShellSkeleton />;

  const courante =
    etape ??
    (siennes !== undefined && siennes.length > 0 ? "application" : "facultes");

  if (courante === "application") return <Application me={me} />;

  return (
    <div className="mx-auto flex min-h-full max-w-[600px] flex-col px-4 pb-10">
      {/* Le tunnel d'accueil est le seul écran qu'un candidat traverse UNE
          fois : ses deux composants sont découpés, et le squelette de liste
          tient lieu d'attente — c'est déjà celui que le catalogue affiche
          pendant sa requête, donc rien ne saute à l'arrivée du code. */}
      <Suspense fallback={<FacultyListSkeleton />}>
        <main className="flex flex-col gap-6 pt-8">
          {/* **Le mot de bienvenue** — [DECISIONS §317]. Il se lit ici et
              nulle part ailleurs : l'écran qui crée le compte disparaît à
              l'instant où le compte existe, et c'est cette page-ci que le
              candidat voit en premier. */}
          <MotDeBienvenue />

          {courante === "facultes" ? (
            // Le catalogue arrive du réseau : sans squelette, le candidat voit
            // le titre surmonter une liste vide et croit qu'il n'y a rien.
            chargementCatalogue ? (
              <FacultyListSkeleton />
            ) : (
              <>
                <FacultyPicker
                  faculties={faculties}
                  reversible
                  busy={choisir.isPending}
                  onSubmit={(slugs, primary, options) => {
                    choisir.mutate({ slugs, primary, options });
                  }}
                />

                {/* RG-D26 — **l'échec se dit.** Ce tunnel n'avait aucune branche
                  d'erreur : session expirée, validation refusée ou réseau
                  coupé, le clic sur « Continuer » ne produisait RIEN — pas de
                  message, pas de bouton grisé. Le candidat retapait, puis
                  concluait que l'application était cassée, et l'inscription
                  s'arrêtait là. */}
                <EchecDuTunnel erreur={choisir.error} />
              </>
            )
          ) : (
            <>
              <ThemesSection />
              <Button
                onClick={() => {
                  setEtape("application");
                }}
              >
                Terminer
              </Button>
            </>
          )}
        </main>
      </Suspense>
    </div>
  );
}

/**
 * Ce qui a empêché d'avancer, dit précisément — RG-D26, [DECISIONS §54].
 *
 * **Chaque cause a sa phrase.** Une session expirée ne se répare pas comme un
 * réseau coupé : le candidat qui lit « vérifiez votre connexion » sur une
 * session expirée testera son forfait pendant dix minutes. Le code de statut
 * dit lequel des quatre cas on a.
 */
function EchecDuTunnel({ erreur }: { erreur: unknown }) {
  if (erreur === null || erreur === undefined) return null;

  const statut = erreur instanceof ApiError ? erreur.status : 0;

  const message =
    statut === 401
      ? "Votre session a expiré. Reconnectez-vous — vos choix seront conservés."
      : statut === 422
        ? "Ce choix n’a pas été accepté : sélectionnez entre une et trois facultés."
        : statut === 429
          ? "Trop de tentatives d’affilée. Attendez une minute et réessayez."
          : "Le choix n’a pas pu être enregistré. Vérifiez votre connexion et réessayez.";

  return (
    <p
      role="alert"
      className="rounded-2xl border p-3 text-[15px]"
      style={{ borderColor: "var(--bordure)", color: "var(--texte-2)" }}
    >
      {message}
    </p>
  );
}

/**
 * L'onglet d'arrivée — normalement le Fil.
 *
 * **`/abonnement` est la première exception, et elle vient de MonCash** —
 * [DECISIONS §165]. C'est l'adresse que Bazik reçoit en `successUrl` et où le
 * candidat retombe après avoir payé. Sans cette ligne il atterrirait sur le
 * Fil, sans un mot sur le paiement qu'il vient de faire — au moment précis où
 * il cherche à savoir si ses 499 gourdes ont servi à quelque chose.
 *
 * **`/coupon/<CODE>` est la seconde** — [DECISIONS §330]. Le lien distribué à
 * une classe. Il ouvre le même écran, pour la même raison : c'est là que le
 * champ « J'ai un code » se trouve, déjà rempli par ce que le lien portait.
 *
 * **Ni l'un ni l'autre n'accorde quoi que ce soit** : l'écran relit l'état
 * auprès du serveur, qui n'a crédité que si le rappel signé ou l'échange du
 * coupon l'ont dit. Taper l'une de ces adresses à la main n'ouvre aucun
 * abonnement.
 */
function ongletDArrivee(): string {
  const chemin = window.location.pathname;

  return chemin === "/abonnement" || codeDuLien(chemin) !== null
    ? "abonnement"
    : "feed";
}

/**
 * Les destinations que l'expiration ferme — [DECISIONS §157, §167, §193].
 *
 * « Ni leçon, ni quiz, ni ressource de formation. » Les archives n'y sont pas
 * (§191), le classement non plus, Mon Cercle non plus (§193) : ces trois-là
 * restent, et c'est ce que le message de fermeture nomme.
 *
 * **Trois de ces quatre entrées sont le miroir exact du middleware `abonne`.**
 * Elles ne décident de RIEN — le serveur refuse de toute façon — elles décident
 * de ce qu'on affiche à la place.
 *
 * **`themes` est la quatrième, et elle n'a pas de miroir côté serveur** — §193.
 * `GET /tags` reste ouvert parce qu'il sert aussi le choix du concours, les
 * préférences et l'onboarding : le fermer aurait cassé trois portes pour en
 * fermer une. Rien derrière lui n'est exploitable une fois l'accès terminé —
 * suivre un thème n'ouvre ni leçon ni quiz —, et c'est donc l'écran seul qui
 * tient la règle ici. Le propriétaire range Thèmes avec le Fil et les
 * Formations ; c'est cette ligne qui l'applique.
 */
const FERMES = ["feed", "courses", "exercices", "themes"];

/** Ce qui est en pause, nommé — jamais « ce contenu ». */
const LIBELLE_FERME: Record<string, string> = {
  feed: "le fil, les quiz et les cartes à revoir",
  courses: "les leçons, leurs quiz et l’examen final",
  exercices: "la banque d’exercices",
  themes: "vos thèmes, vos faiblesses et vos angles morts",
};

function Application({ me }: { me: Me }) {
  const [onglet, setOnglet] = useState(ongletDArrivee);

  // Le Fil se monte à sa première ouverture et n'est plus démonté ensuite.
  //
  // Le drapeau se pose DANS LA NAVIGATION, pas dans un effet : un `setState`
  // au montage déclencherait un second rendu de toute l'application pour une
  // valeur qu'on connaît déjà, et lire un `ref` pendant le rendu donnerait une
  // valeur que React n'observe pas.
  const [filOuvert, setFilOuvert] = useState(onglet === "feed");

  /**
   * L'ATELIER éditorial — RG-D9bis, [DECISIONS §115].
   *
   * **Le back-office est un espace, pas un onglet.** Il s'ouvrait avec la
   * navigation du candidat autour de lui : un relecteur qui traite mille cinq
   * cents clés avait sous les yeux quatre destinations dont aucune ne sert son
   * travail.
   *
   * L'écran candidat quitté est mémorisé À PART : sortir du back-office pour
   * retomber sur le Fil ferait perdre sa place à chaque aller-retour — et les
   * allers-retours sont fréquents, on va vérifier dans le Fil ce qu'on vient de
   * publier.
   */
  const [atelier, setAtelier] = useState<string | null>(null);

  /**
   * Où la banque doit s'ouvrir — [DECISIONS §169].
   *
   * **Le pont du cours traverse deux onglets.** Une section de leçon mène aux
   * exercices de cette section ; les exercices sont une destination de la barre
   * du bas (§146), pas un tiroir des Formations. La coque est donc le seul
   * endroit qui puisse faire les deux gestes — changer d'onglet, et dire où
   * atterrir.
   *
   * **Elle se vide dès qu'on entre par la barre.** Sans cela, le candidat qui
   * revient aux Exercices trois jours plus tard retomberait sur la section
   * qu'il avait ouverte depuis un cours — une destination a une porte
   * d'entrée, pas une mémoire d'ailleurs.
   */
  const [cibleDesExercices, setCibleDesExercices] =
    useState<Emplacement | null>(null);

  /**
   * L'écran candidat à peindre — `null` dans l'atelier.
   *
   * **Sans lui, les deux espaces se superposaient.** `onglet` garde sa valeur
   * pendant qu'on travaille dans l'atelier — c'est voulu, c'est ce qui permet
   * de revenir là où l'on était (§115) — mais les écrans du candidat testent
   * `onglet` et se seraient donc peints SOUS l'atelier, Fil compris.
   */
  const vue = atelier === null ? onglet : null;

  /**
   * Où l'on est, sous une forme que l'historique peut porter — [DECISIONS §293].
   *
   * Deux espaces coexistent — le candidat et l'atelier — et un simple `id` ne
   * dit pas dans lequel on se trouve : `attente` est un écran d'atelier,
   * `themes` un onglet du candidat. Le préfixe lève l'ambiguïté.
   */
  const destination =
    atelier === null ? `onglet:${onglet}` : `atelier:${atelier}`;

  /**
   * Appliquer une destination, SANS toucher à l'historique.
   *
   * C'est le geste que le retour du navigateur rejoue : il revient à un état
   * déjà empilé, et le réempiler ferait avancer l'historique au lieu de le
   * remonter — le bouton retour ne sortirait alors jamais de deux écrans.
   */
  const appliquer = (id: string): void => {
    if (id === "editorial") {
      // On entre par « En attente » : c'est la seule page qui répond à
      // « qu'est-ce que je fais maintenant » (§113 du plan de refonte).
      setAtelier("attente");

      return;
    }

    if (id === "front-office") {
      setAtelier(null);

      return;
    }

    if (atelier !== null) {
      setAtelier(id);

      return;
    }

    if (id === "feed") setFilOuvert(true);

    if (id === "exercices") setCibleDesExercices(null);

    setOnglet(id);
  };

  const naviguer = (id: string): void => {
    appliquer(id);

    const cible =
      id === "editorial"
        ? "atelier:attente"
        : id === "front-office"
          ? `onglet:${onglet}`
          : atelier !== null
            ? `atelier:${id}`
            : `onglet:${id}`;

    // Rester où l'on est n'est pas un déplacement : empiler ici obligerait à
    // appuyer deux fois sur retour pour un écran qu'on n'a jamais quitté.
    if (cible !== destination) window.history.pushState({ hc: cible }, "");
  };

  /**
   * **Le pont vers l'historique du navigateur** — [DECISIONS §293].
   *
   * Cette application n'a pas de routeur : la navigation est un état React, et
   * `window.location` n'était lu qu'au montage. L'historique du navigateur ne
   * contenait donc qu'UNE entrée pour toute la session — et le bouton retour du
   * téléphone quittait le document pour retomber sur celui d'avant, l'écran de
   * connexion. Le candidat perdait sa place et se croyait déconnecté.
   *
   * On n'introduit pas un routeur pour autant : ce pont pose des entrées
   * d'historique et les relit, sans changer d'adresse. C'est le minimum qui
   * rende le geste juste, et il tient en un effet.
   *
   * **Ce qu'il ne couvre pas, et il faut le dire :** les écrans profonds — un
   * cours ouvert, une leçon, un quiz — gardent leur état dans leurs propres
   * composants et n'empilent rien. Depuis l'un d'eux, le retour ramène à
   * l'onglet précédent, et non à l'écran précédent. C'est déjà rester dans
   * l'application ; le reste demande que chacun de ces écrans participe.
   */
  useEffect(() => {
    const etat = window.history.state as { hc?: string } | null;

    // La première entrée se RE-marque, elle ne s'empile pas : l'empiler
    // ajouterait un écran fantôme dont on ne pourrait plus sortir par retour.
    if (etat?.hc === undefined) {
      window.history.replaceState({ hc: destination }, "");
    }

    const retour = (evenement: PopStateEvent): void => {
      const cible = (evenement.state as { hc?: string } | null)?.hc;

      // Une entrée qui n'est pas la nôtre — la vitrine, par exemple — n'a rien
      // à nous dire : on la laisse au navigateur.
      if (cible === undefined) return;

      const separateur = cible.indexOf(":");
      const espace = cible.slice(0, separateur);
      const id = cible.slice(separateur + 1);

      if (espace === "atelier") {
        setAtelier(id);

        return;
      }

      setAtelier(null);

      if (id === "feed") setFilOuvert(true);

      if (id === "exercices") setCibleDesExercices(null);

      setOnglet(id);
    };

    window.addEventListener("popstate", retour);

    return () => {
      window.removeEventListener("popstate", retour);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // **La file hors-ligne s'écoute une fois, pour toute l'application** —
  // RG-9.4, §111. Ici et non dans le Fil : une réponse mise en file peut venir
  // d'un défi ou d'un quiz de section, et une séance peut dormir en file depuis
  // la veille — c'est au démarrage qu'elle doit repartir, pas à l'ouverture
  // d'un écran particulier.
  useEffect(() => brancherLaFile(), []);

  // RG-1.11quater — la date de concours vit sur la faculté choisie, pas sur
  // le compte : c'est celle du candidat qu'il faut lire, pas le catalogue.
  const { data: siennes = [] } = useQuery({
    queryKey: ["me", "faculties"],
    queryFn: auth.myFaculties,
  });

  /**
   * L'accès est-il terminé — [DECISIONS §157, §167].
   *
   * **Lu dans la coque, et non attendu des écrans fermés.** Un écran qui
   * découvre la fermeture par une 402 n'a plus rien à afficher : le Fil rendrait
   * une liste vide, le catalogue une page blanche — c'est-à-dire une panne.
   * C'est la coque qui doit savoir, parce que c'est elle qui décide ce qu'on
   * peint.
   *
   * La requête part en parallèle des autres et ne retarde rien : tant qu'elle
   * n'a pas répondu, les écrans se peignent normalement.
   */
  const { data: abonnement } = useQuery({
    queryKey: ["abonnement"],
    queryFn: abo.abonnement,
    retry: false,
  });

  const accesTermine = abonnement?.data.etat === "expire";

  const versLAbonnement = (): void => {
    setOnglet("abonnement");
  };

  // §193 — les trois portes que le message de fermeture nomme mènent quelque
  // part. Les nommer sans y conduire aurait laissé le candidat les chercher
  // dans une barre du bas qu'il vient précisément de voir refuser.
  const versLesArchives = (): void => {
    setOnglet("archives");
  };
  const versLeClassement = (): void => {
    setOnglet("leaderboard");
  };
  const versLeCercle = (): void => {
    setOnglet("amis");
  };

  return (
    <FournisseurDuVisage>
      <FournisseurDuFil>
        <AppShell
          active={atelier ?? onglet}
          espace={atelier === null ? "candidat" : "editorial"}
          displayName={me.display_name}
          dailyGoalXp={me.preferences.daily_goal_xp}
          // 02 §F7.1 — le rôle vient du serveur ; l'entrée n'est qu'un
          // raccourci vers une porte que le serveur garde de toute façon.
          editorial={me.role !== "candidat"}
          faculties={siennes}
          onNavigate={naviguer}
          header={
            <>
              {/* **Le bandeau de maintenance passe AVANT tous les autres** —
              [DECISIONS §196], RG-9.10. Confirmer son adresse ou renouveler son
              abonnement peut attendre dix minutes ; remettre l'épreuve qu'on a
              commencée avant la coupure, non. C'est aussi le seul bandeau qui
              porte une échéance en minutes. Les heures sont celles du candidat
              (`users.timezone`), jamais celles du serveur. */}
              <BandeauDeMaintenance fuseau={me.preferences.timezone} />
              <VerificationBanner me={me} />
              {/* §167 — le bandeau ne double PAS l'écran de fermeture : deux fois
              le même message sur la même page se lit comme un défaut
              d'affichage. Il sert aux écrans restés ouverts — d'abord les
              archives, qui fonctionnent mais dont les thèmes n'ouvrent plus
              rien. */}
              {accesTermine && !FERMES.includes(vue ?? "") && (
                <BandeauAccesTermine onAbonnement={versLAbonnement} />
              )}
            </>
          }
          // RG-D22 amendée — le visage vit dans l'en-tête, et seulement quand le
          // Fil est à l'écran : commenter la performance devant un écran
          // d'archives n'aurait aucun sens.
          entete={
            atelier === null && onglet === "feed" ? (
              <VisageDeLEntete />
            ) : undefined
          }
          // §88 — le rafraîchissement est une icône, À GAUCHE de l'en-tête. Le
          // visage occupe la droite ; les deux ne se disputent donc rien.
          enteteGauche={
            atelier === null && onglet === "feed" ? (
              <RafraichirDepuisLEntete />
            ) : undefined
          }
        >
          {/* **Le filet** : un composant qui lève pendant le rendu démonterait tout
          l'arbre, et le candidat se retrouverait devant une page blanche sans
          message ni sortie.
          Pas de `key` sur l'onglet : elle remontait tout l'arbre à chaque
          changement, ce qui effaçait le Fil qu'on vient précisément de garder.
          Le bouton « Réessayer » du filet remonte déjà ce qu'il faut. */}
          <Filet
            onAccueil={() => {
              setOnglet("feed");
            }}
          >
            {/* **Le Fil reste MONTÉ, une fois ouvert.** Le démonter effaçait tout ce
          que la séance avait produit — les corrections affichées, les marques
          posées, ce que le visage avait compris de la performance — et aller
          voir les Archives une seconde coûtait quinze cartes de travail.
          Caché en CSS plutôt que retiré du DOM : c'est le seul écran qui porte
          un état qu'on ne peut pas reconstituer d'une requête.
          Mais il ne se monte QU'APRÈS la première visite : le charger d'office
          ferait payer une page de Fil à qui vient consulter ses archives, sur
          un forfait au mégaoctet (PR-2).

          **HORS de la frontière `Suspense`.** Placé dedans, il aurait été
          masqué avec elle à chaque fois qu'un AUTRE écran se charge : React
          cache le contenu d'une frontière suspendue. Le Fil serait remplacé par
          un squelette pendant qu'on ouvre les archives — précisément ce que
          « le Fil reste monté » cherche à éviter. */}
            {filOuvert && !accesTermine && (
              <div hidden={vue !== "feed"}>
                <FeedScreen
                  economieDeDonnees={me.preferences.data_saver}
                  // **`naviguer`, et non `setOnglet`** — [DECISIONS §406].
                  //
                  // Ben, le 8 septembre 2026 : « toutes les invitations à
                  // découvrir des parties de l'app ne sont pas activables ;
                  // lorsque j'ai cliqué sur le bouton ça ne m'a pas emmené sur
                  // la partie à découvrir. »
                  //
                  // La carte posait l'onglet À LA MAIN et sautait donc tout ce
                  // qu'`appliquer()` prépare : ouvrir le Fil (`setFilOuvert`),
                  // remettre à zéro la cible des exercices, et — pour un compte
                  // dans l'ATELIER — router vers l'écran d'atelier plutôt que
                  // vers un onglet que `vue` ne rend même pas. Un administrateur
                  // cliquait sur « Voir » et rien ne bougeait.
                  //
                  // « Y MENER, pas expliquer comment y aller » : la carte le dit
                  // elle-même, encore fallait-il lui donner la porte.
                  onOuvrirOnglet={naviguer}
                />
              </div>
            )}

            {/* §157, §167 — les trois destinations que l'expiration ferme. Le
          serveur les refuse déjà (402) ; ce qui se joue ici est la PHRASE, et
          elle nomme ce qui est en pause plutôt que « ce contenu ». */}
            {accesTermine && vue !== null && FERMES.includes(vue) && (
              <AccesTermine
                ecran={LIBELLE_FERME[vue] ?? ""}
                onAbonnement={versLAbonnement}
                onArchives={versLesArchives}
                onClassement={versLeClassement}
                onCercle={versLeCercle}
              />
            )}

            {/* **Une seule frontière, autour des écrans découpés.** En poser une par
          écran ferait autant de squelettes pour la même attente ; ici la coque
          — rail, en-tête, barre du bas — reste peinte, et seule la colonne se
          remplit : la navigation répond tout de suite, le contenu suit. */}
            <Suspense fallback={<ShellSkeleton />}>
              {/* RG-2.36bis — « Mes faiblesses » est devenu le PREMIER onglet des
          thèmes, au même rang que les deux autres. Sous la liste complète,
          elles étaient invisibles à qui en a le plus besoin : on n'ouvre pas
          Haicademy pour consulter ses lacunes, mais on ouvre l'écran Thèmes
          pour décider quoi réviser — et c'est là qu'elles servent. */}
              {/* `!accesTermine` comme le Fil, les Formations et la Banque — §193.
          Sans lui, l'écran se peindrait SOUS le message de fermeture : deux
          contenus empilés, dont l'un propose des thèmes qui n'ouvrent plus
          rien. */}
              {vue === "themes" && !accesTermine && (
                <ThemesSection
                  avecFaiblesses
                  onOuvrirLeFil={() => {
                    setOnglet("feed");
                  }}
                />
              )}
              {vue === "account" && <AccountPanel me={me} />}

              {/* [DECISIONS §157, §165] — l'essai, l'abonnement, les paiements. Son
          propre écran plutôt qu'un bloc du Compte : il porte un COMPTEUR, et
          celui à qui il reste six heures doit le trouver en deux tapes. */}
              {vue === "abonnement" && <MonAbonnement />}

              {/* RG-1.13, §53 — la bascule de concours. Elle a son écran plutôt qu'un
          bloc dans le compte : c'est elle qui décide du contenu du fil, et la
          ranger avec le mot de passe et les préférences la ferait chercher. */}
              {vue === "concours" && (
                <ConcoursActif
                  onFerme={() => {
                    setOnglet("feed");
                  }}
                />
              )}

              {vue === "courses" && !accesTermine && (
                <ParcoursScreen
                  onExercices={(sectionId) => {
                    setCibleDesExercices({
                      niveau: "section",
                      cle: String(sectionId),
                    });
                    setOnglet("exercices");
                  }}
                />
              )}
              {/* La `key` REMONTE l'écran quand la cible change — §169. Sans elle, un
          second passage par le pont laisserait la banque là où le premier
          l'avait posée : `cible` est l'état INITIAL d'un composant déjà monté,
          et React ne rejoue pas un `useState` sur un simple changement de
          propriété. */}
              {vue === "exercices" && !accesTermine && (
                <ExercicesScreen
                  key={cibleDesExercices?.cle ?? "racine"}
                  cible={cibleDesExercices}
                />
              )}
              {/* Les archives restent — §157. Amputées : leurs thèmes n'ouvrent plus
          rien, et c'est le serveur qui le dit par `tags_actifs`. */}
              {vue === "archives" && <ArchivesScreen />}
              {/* RG-5.14, §193 — le classement reste ENTIER à l'expiration. Il ne
          coûte rien à servir et il est la seule mesure de soi qui survive :
          le fermer aurait retiré au candidat la raison même de revenir. */}
              {vue === "leaderboard" && <Classement />}
              {/* §193 — Mon Cercle reste, amputé : les amis se voient, les partages
          s'annoncent sans s'ouvrir. L'écran lit lui-même l'état de
          l'abonnement, sur la même clé de cache que la coque. */}
              {vue === "amis" && (
                <AmisScreen
                  onAbonnement={versLAbonnement}
                  onArchives={versLesArchives}
                  onClassement={versLeClassement}
                />
              )}
              {/* **L'atelier éditorial** — §115. Il ne s'affiche plus comme un onglet
          parmi ceux du candidat : c'est un ESPACE, et c'est la navigation de la
          coque qui liste ses cinq postes.
          02 §F7.1 — l'entrée n'est PAS le contrôle d'accès : le serveur rend
          404 sur ces routes à qui n'y a pas droit. C'est simplement qu'on ne
          propose pas une porte fermée. */}
              {atelier !== null && (
                <Atelier section={atelier} onSection={setAtelier} />
              )}
              {/* RG-2.45 — l'écran n'est plus un carton d'attente : il porte « Untel a
          répondu à la question que vous lui avez envoyée ». Les rappels de
          révision et les badges viendront s'y ranger. */}
              {/* RG-2.46 — une notification de défi mène AUX défis, qui vivent dans
          Mon Cercle. Sans ce chemin, « untel vous défie » se lirait sans mener
          nulle part — exactement ce que §75 a corrigé pour les questions. */}
              {vue === "notifications" && (
                <NotificationsScreen
                  onOuvrirLesDefis={() => {
                    setOnglet("amis");
                  }}
                />
              )}
            </Suspense>
          </Filet>
        </AppShell>
      </FournisseurDuFil>
    </FournisseurDuVisage>
  );
}

// `Vide` a été retiré le 7 août 2026 : il ne servait plus qu'à l'écran des
// notifications, qui porte désormais une vraie boîte (RG-2.45) — et qui dit
// lui-même ce qui manque quand elle est vide, comme le veut RG-D16.
